import { Divider, Flex, NumberFormat, Space, Spacing, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { getInvoiceSummaryTotals } from '@features/invoices/utils/getInvoiceSummaryTotals'
import { InvoiceDiscountCalculator } from '@features/invoices/utils/invoiceDiscountCalculator'

import { useFormContext, useWatch } from '../../../../hooks'
import { useDemoInvoiceLocale } from '../../hooks/useDemoInvoiceLocale'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'
import { DemoInvoiceGlobalDiscount } from '../DemoInvoiceGlobalDiscount'
import * as Styled from './styles'

interface DemoInvoiceSummaryProps {
  discountMode: InvoiceDiscountMode
}

export const DemoInvoiceSummary = ({ discountMode }: DemoInvoiceSummaryProps): ReactElement => {
  const { t } = useTranslation()
  const locale = useDemoInvoiceLocale()
  const { control } = useFormContext<DemoInvoiceFormSchema>()
  const currency = useWatch({ control, name: 'currency' })
  const discount = useWatch({ control, name: 'discount' })
  const discountType = useWatch({ control, name: 'discountType' })
  const invoiceLines = useWatch({ control, name: 'invoiceLines' })

  const isGlobalDiscount = discountMode === InvoiceDiscountMode.Global
  const shouldDisplayDiscountSummary = discountMode !== InvoiceDiscountMode.None

  const { discountTotal, grossTotal, netTotal, subTotal, taxSummary, validationIssues } = getInvoiceSummaryTotals({
    currency,
    discountType,
    globalDiscount: isGlobalDiscount ? discount : undefined,
    invoiceLines,
  })
  const hasGlobalDiscountError = InvoiceDiscountCalculator.hasDiscountExceedsBaseIssue(validationIssues, -1)

  return (
    <Flex alignItems="flex-end" flexDirection="column">
      <Styled.SummaryContent>
        {isGlobalDiscount && <DemoInvoiceGlobalDiscount hasDiscountError={hasGlobalDiscountError} />}
        {shouldDisplayDiscountSummary && (
          <>
            <Flex flexDirection="column" gap={Spacing.S8}>
              <Flex justifyContent="space-between">
                <Text colorVariant="secondary" variant="bodySM">
                  {t('external_invoices.editor.summary.subtotal_excluding_vat')}
                </Text>
                <Text colorVariant="secondary" variant="bodySM">
                  {NumberFormat.getCurrencyNumber(subTotal, currency, locale)}
                </Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text colorVariant="secondary" variant="bodySM">
                  {t('external_invoices.editor.summary.discount_amount')}
                </Text>
                <Text colorVariant="secondary" variant="bodySM">
                  {NumberFormat.getCurrencyNumber(discountTotal, currency, locale)}
                </Text>
              </Flex>
              <Divider />
            </Flex>
            <Space size="s8" />
          </>
        )}
        <Flex flexDirection="column" gap={Spacing.S8}>
          <Flex justifyContent="space-between">
            <Text colorVariant="secondary" variant="bodySM">
              {t('external_invoices.editor.summary.total_excluding_vat')}
            </Text>
            <Text colorVariant="secondary" variant="bodySM">
              {NumberFormat.getCurrencyNumber(netTotal, currency, locale)}
            </Text>
          </Flex>
          {taxSummary.map(({ amount, percentage }) => (
            <Flex justifyContent="space-between" key={percentage}>
              <Text colorVariant="secondary" variant="bodySM">
                {t('external_invoices.editor.summary.vat', {
                  percentage: NumberFormat.getPercentNumber(percentage, locale),
                })}
              </Text>
              <Text colorVariant="secondary" variant="bodySM">
                {NumberFormat.getCurrencyNumber(amount, currency, locale)}
              </Text>
            </Flex>
          ))}
        </Flex>
        <Space size="s8" />
        <Divider />
        <Space size="s8" />
        <Flex justifyContent="space-between">
          <Text weight="bold">{t('external_invoices.editor.summary.total')}</Text>
          <Text variant="h4" weight="bold">
            {NumberFormat.getCurrencyNumber(grossTotal, currency, locale)}
          </Text>
        </Flex>
      </Styled.SummaryContent>
    </Flex>
  )
}
